// src/pages/employee/EmployeeGoalView.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertCircle,
  ArrowLeft,
  BarChart3,
  Calendar,
  CheckCircle2,
  Flag,
  Target,
} from "lucide-react";
import { toast } from "sonner";
import { getGoalById } from "@/services/goalService";

const EmployeeGoalView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const backTo = location.state?.returnTo || "/employee/reviews?tab=goals";

  const [goal, setGoal] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadGoal();
  }, [id]);

  const loadGoal = async () => {
    try {
      setLoading(true);

      const response = await getGoalById(id);
      const goalData = response?.data || response;
      setGoal(goalData || null);
    } catch (error) {
      console.error("Error fetching goal:", error);
      toast.error("Failed to load goal details");
      setGoal(null);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (value) => {
    if (!value) return "N/A";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "N/A";

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const calculateDurationDays = (startDate, endDate) => {
    if (!startDate || !endDate) return "N/A";

    const start = new Date(startDate);
    const end = new Date(endDate);
    const now = new Date();

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return "N/A";
    }

    // If already past due
    if (now > end) {
      const overdueDays = Math.ceil((now - end) / (1000 * 60 * 60 * 24));
      return `${overdueDays} days overdue`;
    }

    // If still ongoing → calculate remaining days
    const remainingDays = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return `${remainingDays} days left`;
  };

  const getKeyResultTitle = (keyResult) => {
    return keyResult?.title || keyResult?.text || keyResult?.label || "Untitled key result";
  };

  const getKeyResultProgress = (keyResult) => {
    const value = keyResult?.progress ?? keyResult?.current ?? 0;
    const numericValue = Number(value);

    if (Number.isNaN(numericValue)) return 0;

    return Math.min(Math.max(numericValue, 0), 100);
  };

  const isGoalOverdue = (goalData) => {
    if (!goalData?.dueDate && !goalData?.endDate) return false;
    if (goalData?.status === "Completed") return false;

    const dueDate = new Date(goalData.dueDate || goalData.endDate);
    if (Number.isNaN(dueDate.getTime())) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);

    return dueDate < today;
  };

  const keyResults = useMemo(() => {
    if (!goal?.keyResults || !Array.isArray(goal.keyResults)) return [];

    return goal.keyResults;
  }, [goal]);

  const goalProgress = useMemo(() => {
    if (!goal) return 0;

    const directProgress = Number(goal.progress);
    if (!Number.isNaN(directProgress)) {
      return Math.min(Math.max(directProgress, 0), 100);
    }

    if (keyResults.length === 0) return 0;

    const total = keyResults.reduce((sum, keyResult) => {
      return sum + getKeyResultProgress(keyResult);
    }, 0);

    return Math.round(total / keyResults.length);
  }, [goal, keyResults]);

  const completedKeyResults = useMemo(() => {
    return keyResults.filter((keyResult) => getKeyResultProgress(keyResult) >= 100).length;
  }, [keyResults]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold tracking-tight text-foreground">
            Goal Details
          </h1>
          <p className="text-muted-foreground mt-1">Loading goal details...</p>
        </div>

        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            Loading...
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!goal) {
    return (
      <div className="space-y-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold tracking-tight text-foreground">
            Goal Details
          </h1>
        </div>

        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <p className="text-lg">Goal record not found.</p>

            <Button
              variant="outline"
              className="mt-4"
              onClick={() => navigate(backTo)}
            >
              Back to Goals
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const dueDate = goal.dueDate || goal.endDate;
  const startDate = goal.startDate;
  const overdue = isGoalOverdue(goal);
  const isCompleted = goal.status === "Completed";

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <Button
          variant="ghost"
          size="sm"
          className="mb-4"
          onClick={() => navigate(backTo)}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Goals
        </Button>

        {overdue && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-800 dark:bg-red-950/30 dark:text-red-300 mb-4">
            This goal is overdue and requires attention.
          </div>
        )}

        {isCompleted && (
          <Card className="border-primary/20 dark:border-primary bg-primary/10 dark:bg-primary/10 mb-4">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <h4 className="text-sm font-semibold text-primary">
                    Goal Completed Successfully
                  </h4>
                  <p className="text-xs text-primary mt-1">
                    This goal has been completed and finalized.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
              {goal.title || "N/A"}
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground mt-1">
              View your assigned goal, progress, key results, and feedback.
            </p>
          </div>

        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Target className="h-5 w-5 text-primary" />
              Goal Information
            </CardTitle>
          </CardHeader>

          <CardContent>
            <div className="space-y-3 text-sm">

              <Separator />

              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Type :</span>
                <span className="font-medium text-right">{goal.type || "N/A"}</span>
              </div>

              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground truncate">Project :</span>
                {goal.project || "Unassigned"}
              </div>

              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Priority :</span>
                <span className="font-medium text-right">
                  {goal.priority || "N/A"}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              Timeline
            </CardTitle>
          </CardHeader>

          <CardContent>
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between gap-4">
                <span className="text-muted-foreground">Start Date</span>
                <span className="font-medium text-right">{formatDate(startDate)}</span>
              </div>

              <div className="flex items-center justify-between gap-4">
                <span className="text-muted-foreground">Due Date</span>
                <span
                  className={`font-medium text-right ${overdue ? "text-red-600 dark:text-red-400" : ""
                    }`}
                >
                  {formatDate(dueDate)}
                </span>
              </div>

              <Separator />

              <div className="flex items-center justify-between gap-4">
                <span className="text-muted-foreground">Duration</span>
                <span className="font-medium text-right">
                  {isCompleted ? "Already completed" : calculateDurationDays(startDate, dueDate)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-primary" />
              Progress
            </CardTitle>
          </CardHeader>

          <CardContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Overall Progress</span>
                  <span className="font-medium">{goalProgress}%</span>
                </div>

                <Progress value={goalProgress} className="h-2" />

                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>
                    {completedKeyResults} of {keyResults.length} key results completed
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-primary" />
            Goal Description
          </CardTitle>
        </CardHeader>

        <CardContent>
          {goal.description ? (
            <p className="text-sm text-foreground whitespace-pre-wrap truncate">
              {goal.description}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground truncate">
              No description provided.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Flag className="h-5 w-5 text-primary" />
            Key Results / Milestones
          </CardTitle>
          <CardDescription>
            Track the progress of your assigned goal milestones.
          </CardDescription>
        </CardHeader>

        <CardContent>
          {keyResults.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              <p>No key results defined for this goal.</p>
            </div>
          ) : (
            <div
              className="border border-gray-200 dark:border-gray-700 rounded-md overflow-x-auto thin-scrollbar"
              style={{ scrollbarWidth: "thin" }}
            >
              <div className="min-w-[800px]" style={{ scrollbarWidth: "thin" }}>
                <Table>
                  <TableHeader>
                    <TableRow className="border-b border-gray-200 dark:border-gray-700">
                      <TableHead className="w-14 text-gray-700 dark:text-gray-300">
                        #
                      </TableHead>
                      <TableHead className="w-[35%] text-gray-700 dark:text-gray-300">
                        Key Result
                      </TableHead>
                      <TableHead className="w-[45%] text-gray-700 dark:text-gray-300">
                        Progress
                      </TableHead>
                      <TableHead className="w-[20%] text-gray-700 dark:text-gray-300">
                        Status
                      </TableHead>
                    </TableRow>
                  </TableHeader>

                  <TableBody>
                    {keyResults.map((keyResult, index) => {
                      const progress = getKeyResultProgress(keyResult);
                      const isDone = progress >= 100;

                      return (
                        <TableRow
                          key={keyResult.id || keyResult._id || index}
                          className="border-b border-gray-200 dark:border-gray-700"
                        >
                          <TableCell className="text-gray-800 dark:text-gray-300">
                            <div
                              className={`h-7 w-7 rounded-full flex items-center justify-center ${isDone
                                ? "bg-primary text-primary-foreground"
                                : "bg-muted text-muted-foreground"
                                }`}
                            >
                              {isDone ? (
                                <CheckCircle2 className="h-4 w-4" />
                              ) : (
                                <span className="text-xs font-semibold">
                                  {index + 1}
                                </span>
                              )}
                            </div>
                          </TableCell>

                          <TableCell>
                            <div className="font-medium text-gray-900 dark:text-gray-200 truncate">
                              {getKeyResultTitle(keyResult)}
                            </div>
                          </TableCell>

                          <TableCell>
                            <div className="flex items-center gap-2 max-w-xs">
                              <Progress value={progress} className="h-2 flex-1" />
                              <span className="text-xs font-semibold min-w-[35px] text-right">
                                {progress}%
                              </span>
                            </div>
                          </TableCell>

                          <TableCell>
                            <Badge
                              variant="outline"
                              className={`text-[10px] ${isDone
                                ? "bg-primary text-primary-foreground border-primary dark:bg-primary dark:border-primary"
                                : "bg-muted text-muted-foreground border-muted-foreground/20 dark:bg-muted/30 dark:text-muted-foreground dark:border-muted-foreground/30"
                                }`}
                            >
                              {isDone ? "Completed" : "Pending"}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EmployeeGoalView;